import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { StoriesDataService, Story } from '../../../services/stories-data.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LayoutService } from '../../../shared/components/app-layout/shared-layout/layout.service';
import * as AOS from 'aos';
interface FilterOptions {
  category: string;
  sortBy: 'newest' | 'oldest' | 'popular' | 'title';
  readTime: 'all' | 'short' | 'medium' | 'long';
}
@Component({
  selector: 'app-stories-section',
  templateUrl: './stories-section.component.html',
  styleUrls: ['./stories-section.component.scss']
})
export class StoriesSectionComponent implements OnInit, OnDestroy {
  
     private destroy$ = new Subject<void>();
  private readonly storiesDataService = inject(StoriesDataService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  // Data
  stories: Story[] = [];
  filteredStories: Story[] = [];
  displayedStories: Story[] = [];
  categories: string[] = [];
  
  // UI States
  loading = false;
  searchQuery = '';
  viewMode: 'grid' | 'list' = 'grid';
  
  // Filters
  filters: FilterOptions = {
    category: 'all',
    sortBy: 'newest',
    readTime: 'all'
  };

  // Pagination
   currentPage = 1;
  itemsPerPage = 12;
  totalPages = 1;
  jumpToPage: number = 1; // Add this property


  ngOnInit() {
    this.loadAllStories();
    
    // Check if there's a search query from route params
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['search']) {
        this.searchQuery = params['search'];
      }
    });
  }

  ngAfterViewInit() {
    AOS.init({
      duration: 800,
      easing: 'ease-in-out',
      once: true
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------- DATA LOADING ----------------

  loadAllStories() {
    this.loading = true;

    this.storiesDataService
      .getStories({ itemsPerPage: 1000 }) // Load all stories
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stories) => {
          this.stories = stories;
          this.extractCategories();
          this.applyFilters();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading stories:', error);
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
  }

  extractCategories() {
    const categorySet = new Set<string>();
    this.stories.forEach(story => {
      if (story.category) {
        categorySet.add(story.category);
      }
    });
    this.categories = Array.from(categorySet).sort();
  }

  // ---------------- FILTERING & SEARCH ----------------

  onSearchChange() {
    this.currentPage = 1;
    this.applyFilters();
  }

  onFilterChange() {
    this.currentPage = 1;
    this.applyFilters();
  }

  applyFilters() {
    let filtered = [...this.stories];

    // Apply search
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(story =>
        story.title.toLowerCase().includes(query) ||
        story.subtitle?.toLowerCase().includes(query) ||
        story.author?.toLowerCase().includes(query) ||
        story.category?.toLowerCase().includes(query)
      );
    }

    // Apply category filter
    if (this.filters.category !== 'all') {
      filtered = filtered.filter(story => 
        story.category === this.filters.category
      );
    }

    // Apply read time filter
    // if (this.filters.readTime !== 'all') {
    //   filtered = filtered.filter(story => {
    //     const readTime = story.readTime || 0;
    //     switch (this.filters.readTime) {
    //       case 'short': return readTime <= 5;
    //       case 'medium': return readTime > 5 && readTime <= 15;
    //       case 'long': return readTime > 15;
    //       default: return true;
    //     }
    //   });
    // }

    // Apply sorting
    filtered = this.sortStories(filtered);

    this.filteredStories = filtered;
    this.updatePagination();
  }

  sortStories(stories: Story[]): Story[] {
    switch (this.filters.sortBy) {
      case 'newest':
        return stories.sort((a, b) => 
          new Date(b.timestamp || '').getTime() - new Date(a.timestamp || '').getTime()
        );
      case 'oldest':
        return stories.sort((a, b) => 
          new Date(a.timestamp || '').getTime() - new Date(b.timestamp || '').getTime()
        );
     
      case 'title':
        return stories.sort((a, b) => a.title.localeCompare(b.title));
      default:
        return stories;
    }
  }

  clearSearch() {
    this.searchQuery = '';
    this.onSearchChange();
  }

  clearAllFilters() {
    this.searchQuery = '';
    this.filters = {
      category: 'all',
      sortBy: 'newest',
      readTime: 'all'
    };
    this.currentPage = 1;
    this.applyFilters();
  }

  // ---------------- PAGINATION ----------------

  updatePagination() {
    this.totalPages = Math.ceil(this.filteredStories.length / this.itemsPerPage);
    this.updateDisplayedStories();
  }

  updateDisplayedStories() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.displayedStories = this.filteredStories.slice(startIndex, endIndex);
    this.scrollToTop();
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updateDisplayedStories();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updateDisplayedStories();
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updateDisplayedStories();
    }
  }

  get paginationArray(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    
    if (this.totalPages <= maxVisible) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (this.currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push(-1); // Ellipsis
        pages.push(this.totalPages);
      } else if (this.currentPage >= this.totalPages - 2) {
        pages.push(1);
        pages.push(-1);
        for (let i = this.totalPages - 3; i <= this.totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push(-1);
        for (let i = this.currentPage - 1; i <= this.currentPage + 1; i++) pages.push(i);
        pages.push(-1);
        pages.push(this.totalPages);
      }
    }
    
    return pages;
  }

  // ---------------- UI HELPERS ----------------

  toggleViewMode() {
    this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid';
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  openStory(story: Story) {
    this.router.navigate(['/story', story.uniqueId]);
  }

  onImageError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/images/default-magazine.jpg';
  }

  trackByStoryId(_: number, story: Story): number {
    return story.id;
  }

  goBack() {
    this.router.navigate(['/']);
  }

  get hasActiveFilters(): boolean {
    return this.searchQuery !== '' || 
           this.filters.category !== 'all' || 
           this.filters.readTime !== 'all';
  }

  get resultsText(): string {
    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(this.currentPage * this.itemsPerPage, this.filteredStories.length);
    return `Showing ${start}-${end} of ${this.filteredStories.length} stories`;
  }
}